import { createContext, useContext, useState, useCallback, useEffect, useMemo, Dispatch, SetStateAction, ReactNode, useRef } from "react";
import scraperApi from "api";
import { useDebouncer } from "hooks/useDebouncer";
import { useAbortController } from "hooks/useAbortController";
import { ErrorLogsData, ErrorLogEntry, EnrichedErrorLogEntry } from "utils/analytics/types";
import { buildAnalyticsApiParams } from "utils/analytics/filters";
import { ERROR_LOGS_PAGE_SIZE, DEBOUNCE_DELAY } from "utils/analytics/constants";
import { useAnalyticsFilters } from "./AnalyticsFilterProvider";
import { createPageCache, pageCacheKey } from "utils/analytics/pageCache";
import { useLoading } from "hooks/useLoading";
import { useToast } from "hooks/useToast";

interface ErrorLogsContextType {
  errorLogsData: ErrorLogsData;
  setErrorLogsData: Dispatch<SetStateAction<ErrorLogsData>>;
  loading: boolean;

  setSearchTerm: (searchTerm: string) => void;

  goToPage: (page: number) => void;
  hasNextPage: boolean;
  errorLogsError: string | null;
  retryErrorLogsPage: () => void;

  showHidden: boolean;
  setShowHidden: (show: boolean) => void;

  // Hidden-row state lives in the provider so it survives pagination + cache hits
  setHidden: (id: string, hidden: boolean) => void;
  setHiddenBatch: (ids: string[], hidden: boolean) => void;
}

const ErrorLogsContext = createContext<ErrorLogsContextType | undefined>(undefined);

interface ErrorLogsProviderProps {
  children: ReactNode;
}

const EMPTY_ERROR_LOGS_DATA: ErrorLogsData = {
  list: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: ERROR_LOGS_PAGE_SIZE,
  filterOptions: {
    domains: [],
    statusCodes: [],
  },
};

const enrichErrorLog = (entry: ErrorLogEntry, hiddenIds: Set<string>): EnrichedErrorLogEntry => ({
  ...entry,
  isSelected: false,
  isHidden: hiddenIds.has(entry.request_id),
});

const applyHiddenIds = (
  list: EnrichedErrorLogEntry[],
  hiddenIds: Set<string>,
): EnrichedErrorLogEntry[] =>
  list.map((item) =>
    item.isHidden === hiddenIds.has(item.request_id)
      ? item
      : { ...item, isHidden: hiddenIds.has(item.request_id) },
  );

export const ErrorLogsProvider = ({ children }: ErrorLogsProviderProps) => {
  const { dateRange, errorLogsFilters, setErrorLogsFilters } = useAnalyticsFilters();
  const {
    createNewSignal: createNewErrorLogsSignal,
    shouldIgnoreError: shouldIgnoreErrorLogsError,
  } = useAbortController();
  const { showError } = useToast();

  // Pagination cache lives in the provider — cleared on unmount (route change, logout)
  const [errorLogsCache] = useState(() => createPageCache<ErrorLogsData>());

  const [errorLogsData, setErrorLogsData] = useState<ErrorLogsData>(EMPTY_ERROR_LOGS_DATA);
  const [errorLogsError, setErrorLogsError] = useState<string | null>(null);

  const { loading, setLoading } = useLoading();

  // UI state - show hidden rows
  const [showHidden, setShowHidden] = useState<boolean>(false);

  // Hidden request IDs are kept in a ref so the fetch/cache-hit paths can
  // re-derive isHidden on every read without mutating cached snapshots.
  // setErrorLogsData (called alongside in the helpers below) drives re-renders.
  const hiddenIdsRef = useRef<Set<string>>(new Set());

  // Debounced filters
  const debouncedErrorLogsFilters = useDebouncer(errorLogsFilters, DEBOUNCE_DELAY);
  const debouncedDateRange = useDebouncer(dateRange, DEBOUNCE_DELAY);

  // Fetch error logs page
  const fetchErrorLogsPage = useCallback(
    async (page: number = 1, forceRefetch: boolean = false) => {
      if (!dateRange[0] || !dateRange[1]) return;

      // Abort any in-flight request first, so a late response can't overwrite what we show next
      const signal = createNewErrorLogsSignal();

      const cacheKey = pageCacheKey(dateRange, errorLogsFilters);
      if (errorLogsCache.isNewKey(cacheKey) && !forceRefetch) {
        setErrorLogsData(EMPTY_ERROR_LOGS_DATA);
      }

      const cachedData = forceRefetch ? undefined : errorLogsCache.get(cacheKey, page);
      if (cachedData) {
        // Re-derive isHidden against the current hiddenIds — the cached snapshot
        // may have been stored with an older view of hidden rows.
        setErrorLogsData({
          ...cachedData,
          list: applyHiddenIds(cachedData.list, hiddenIdsRef.current),
        });
        setErrorLogsError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorLogsError(null);

      // Global filters don't apply to error logs: date range + error-log filters only
      const params = buildAnalyticsApiParams(dateRange, undefined, {
        page,
        pageSize: ERROR_LOGS_PAGE_SIZE,
        ...errorLogsFilters,
      });

      try {
        const response = await scraperApi.analytics.getErrorLogs(params, { signal });

        if (signal.aborted) {
          return;
        }

        const data = response?.data;
        const currentHidden = hiddenIdsRef.current;
        const newData: ErrorLogsData = {
          list: (data?.errors || []).map((e) => enrichErrorLog(e, currentHidden)),
          totalCount: data?.pagination?.total || 0,
          currentPage: data?.pagination?.page || 1,
          pageSize: data?.pagination?.pageSize || ERROR_LOGS_PAGE_SIZE,
          filterOptions: {
            domains: data?.filterOptions?.domains || [],
            statusCodes: (data?.filterOptions?.statusCodes || []).map(String),
          },
        };

        errorLogsCache.set(cacheKey, page, newData);

        setErrorLogsData(newData);
      } catch (err: any) {
        if (shouldIgnoreErrorLogsError(err)) return;

        setErrorLogsData({
          ...EMPTY_ERROR_LOGS_DATA,
          currentPage: page,
        });
        setErrorLogsError(
          err instanceof Error ? err.message : "Failed to load error logs"
        );

        showError(
          "Failed to load error logs",
          "There was an error loading error logs. Please try again.",
        );
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [
      dateRange,
      errorLogsFilters,
      createNewErrorLogsSignal,
      errorLogsCache,
      shouldIgnoreErrorLogsError,
      showError,
      setLoading,
    ],
  );

  // Fetch page 1 once the debounced filters settle. fetchErrorLogsPage changes on every
  // keystroke (it reads the live filters), so adding it here would defeat the debounce.
  useEffect(() => {
    fetchErrorLogsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedErrorLogsFilters, debouncedDateRange]);

  const hasNextPage = useMemo(
    () => errorLogsData.currentPage * errorLogsData.pageSize < errorLogsData.totalCount,
    [errorLogsData.currentPage, errorLogsData.pageSize, errorLogsData.totalCount],
  );

  const goToPage = useCallback(
    (page: number) => {
      fetchErrorLogsPage(page);
    },
    [fetchErrorLogsPage],
  );

  const retryErrorLogsPage = useCallback(() => {
    fetchErrorLogsPage(errorLogsData.currentPage, true);
  }, [fetchErrorLogsPage, errorLogsData.currentPage]);

  const setSearchTerm = useCallback(
    (searchTerm: string) => {
      setErrorLogsFilters({ searchTerm });
    },
    [setErrorLogsFilters],
  );

  const setHidden = useCallback((id: string, hidden: boolean) => {
    const next = new Set(hiddenIdsRef.current);
    if (hidden) next.add(id);
    else next.delete(id);
    hiddenIdsRef.current = next;

    setErrorLogsData((prev) => ({
      ...prev,
      list: prev.list.map((item) =>
        item.request_id === id
          ? { ...item, isHidden: hidden, isSelected: false }
          : item,
      ),
    }));
  }, []);

  const setHiddenBatch = useCallback((ids: string[], hidden: boolean) => {
    if (ids.length === 0) return;

    const next = new Set(hiddenIdsRef.current);
    if (hidden) ids.forEach((id) => next.add(id));
    else ids.forEach((id) => next.delete(id));
    hiddenIdsRef.current = next;

    const idSet = new Set(ids);
    setErrorLogsData((prev) => ({
      ...prev,
      list: prev.list.map((item) =>
        idSet.has(item.request_id)
          ? { ...item, isHidden: hidden, isSelected: false }
          : item,
      ),
    }));
  }, []);

  const value = useMemo<ErrorLogsContextType>(
    () => ({
      errorLogsData,
      setErrorLogsData,
      loading,
      setSearchTerm,
      goToPage,
      hasNextPage,
      errorLogsError,
      retryErrorLogsPage,
      showHidden,
      setShowHidden,
      setHidden,
      setHiddenBatch,
    }),
    [errorLogsData, loading, setSearchTerm, goToPage, hasNextPage, errorLogsError, retryErrorLogsPage, showHidden, setHidden, setHiddenBatch],
  );

  return (
    <ErrorLogsContext.Provider value={value}>
      {children}
    </ErrorLogsContext.Provider>
  );
};

export const useErrorLogs = () => {
  const context = useContext(ErrorLogsContext);
  if (context === undefined) {
    throw new Error("useErrorLogs must be used within an ErrorLogsProvider");
  }
  return context;
};
